import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Brain,
  Info,
  Trophy,
  Clock,
  HelpCircle,
  XCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  setDoc,
  runTransaction,
  increment,
} from "firebase/firestore";
import { db } from "../../utils/firebase";
import { useAuth } from "../../hooks/AuthenticationProvider";

const Minesweeper = () => {
  const navigate = useNavigate();
  const { user, organizationId } = useAuth();

  // ----------------------------------
  //           GAME STATES
  // ----------------------------------
  const [gameMode, setGameMode] = useState("intro"); // "intro" | "tutorial" | "play" | "results"
  const [difficulty, setDifficulty] = useState(1); // 1 (Easy), 2 (Medium), 3 (Hard)
  const [gameStarted, setGameStarted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const timerRef = useRef(null);
  const [finalScore, setFinalScore] = useState(0);

  // ----------------------------------
  //          BOARD CONFIGURATION
  // ----------------------------------
  const rows = 10;
  const cols = 10;
  // Determine mine count based on selected difficulty.
  const getMineCount = () => {
    if (difficulty === 1) return 10;
    if (difficulty === 2) return 15;
    if (difficulty === 3) return 20;
  };
  const [mineCount, setMineCount] = useState(getMineCount());
  // Board will be a 2D array of cell objects.
  // Each cell: { mine: boolean, revealed: boolean, flagged: boolean, adjacent: number }
  const [board, setBoard] = useState([]);
  const [gameOver, setGameOver] = useState(false);
  const [gameWin, setGameWin] = useState(false);
  const [score, setScore] = useState(0);

  // ----------------------------------
  //         HIGH SCORES FETCH
  // ----------------------------------
  const [highScores, setHighScores] = useState([]);

  useEffect(() => {
    if (!organizationId) return;
    const fetchScores = async () => {
      try {
        const docRef = doc(
          db,
          `organizations/${organizationId}/gameScores`,
          "old-school-minesweeper"
        );
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          const scoresForDifficulty =
            data.leaderboards && data.leaderboards[difficulty.toString()]
              ? data.leaderboards[difficulty.toString()]
              : [];
          setHighScores(scoresForDifficulty);
        } else {
          setHighScores([]);
        }
      } catch (err) {
        console.error("Error fetching high scores:", err);
      }
    };
    fetchScores();
  }, [organizationId, difficulty]);

  // ----------------------------------
  //           TIMER LOGIC
  // ----------------------------------
  useEffect(() => {
    if (isPlaying && !isPaused) {
      timerRef.current = setInterval(() => {
        setTimeElapsed((prev) => prev + 1);
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isPlaying, isPaused]);

  // ----------------------------------
  //         BOARD INITIALIZATION
  // ----------------------------------
  const initializeBoard = () => {
    // Create an empty board.
    let newBoard = Array(rows)
      .fill()
      .map(() =>
        Array(cols)
          .fill()
          .map(() => ({
            mine: false,
            revealed: false,
            flagged: false,
            adjacent: 0,
          }))
      );

    // Place mines randomly.
    let minesToPlace = getMineCount();
    while (minesToPlace > 0) {
      const r = Math.floor(Math.random() * rows);
      const c = Math.floor(Math.random() * cols);
      if (!newBoard[r][c].mine) {
        newBoard[r][c].mine = true;
        minesToPlace--;
      }
    }

    // Compute adjacent mine counts for each cell.
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (!newBoard[r][c].mine) {
          let count = 0;
          for (let i = -1; i <= 1; i++) {
            for (let j = -1; j <= 1; j++) {
              const nr = r + i;
              const nc = c + j;
              if (
                nr >= 0 &&
                nr < rows &&
                nc >= 0 &&
                nc < cols &&
                newBoard[nr][nc].mine
              ) {
                count++;
              }
            }
          }
          newBoard[r][c].adjacent = count;
        }
      }
    }

    return newBoard;
  };

  // ----------------------------------
  //         GAME PLAY LOGIC
  // ----------------------------------

  // Recursive function to reveal cells.
  const revealCell = (r, c, boardCopy) => {
    if (r < 0 || r >= rows || c < 0 || c >= cols) return;
    const cell = boardCopy[r][c];
    if (cell.revealed || cell.flagged) return;
    cell.revealed = true;
    if (cell.adjacent === 0 && !cell.mine) {
      for (let i = -1; i <= 1; i++) {
        for (let j = -1; j <= 1; j++) {
          if (i !== 0 || j !== 0) {
            revealCell(r + i, c + j, boardCopy);
          }
        }
      }
    }
  };

  // Handle left-click to reveal a cell.
  const handleCellClick = (r, c) => {
    if (!isPlaying || isPaused || gameOver || gameWin) return;
    setBoard((prevBoard) => {
      const boardCopy = prevBoard.map((row) =>
        row.map((cell) => ({ ...cell }))
      );
      const cell = boardCopy[r][c];
      if (cell.revealed || cell.flagged) return boardCopy;
      if (cell.mine) {
        // Reveal all mines if a mine is clicked.
        for (let i = 0; i < rows; i++) {
          for (let j = 0; j < cols; j++) {
            if (boardCopy[i][j].mine) boardCopy[i][j].revealed = true;
          }
        }
        setGameOver(true);

        const finalScore = calculateScore();
        setScore(finalScore);
      } else {
        revealCell(r, c, boardCopy);
      }
      // Check if win condition is met (all non-mine cells revealed).
      let won = true;
      for (let i = 0; i < rows; i++) {
        for (let j = 0; j < cols; j++) {
          if (!boardCopy[i][j].mine && !boardCopy[i][j].revealed) {
            won = false;
            break;
          }
        }
        if (!won) break;
      }
      if (won) {
        setGameWin(true);
        setIsPlaying(false);

        const { score } = calculateMinesweeperScore(
          boardCopy,
          timeElapsed,
          difficulty
        );
        setFinalScore(score);
      }

      return boardCopy;
    });
  };

  // Handle right-click to flag or unflag a cell.
  const handleCellRightClick = (e, r, c) => {
    e.preventDefault();
    if (!isPlaying || isPaused || gameOver || gameWin) return;
    setBoard((prevBoard) => {
      const boardCopy = prevBoard.map((row) =>
        row.map((cell) => ({ ...cell }))
      );
      const cell = boardCopy[r][c];
      if (!cell.revealed) {
        cell.flagged = !cell.flagged;
      }
      return boardCopy;
    });
  };

  // ----------------------------------
  //         START & END GAME
  // ----------------------------------
  const startGame = () => {
    setTimeElapsed(0);
    setGameOver(false);
    setGameWin(false);
    setBoard(initializeBoard());
    setIsPlaying(true);
    setIsPaused(false);
  };

  const endGame = () => {
    setIsPlaying(false);
    const finalScore = calculateScore();
    setScore(finalScore);

    setGameMode("results");
  };

  // ----------------------------------
  //         SAVE SCORE
  // ----------------------------------

  const calculateScore = () => {
    let revealedCount = 0;
    for (let row of board) {
      for (let cell of row) {
        if (cell.revealed && !cell.mine) {
          revealedCount++;
        }
      }
    }

    const maxPossible = rows * cols - getMineCount();

    // Reduce revealedScore scale
    const revealedScore = (revealedCount / maxPossible) * 150;

    // Heavily reduce time bonus scale
    const cappedTime = Math.min(timeElapsed, 120);
    const timeScore = 30 - (cappedTime / 120) * 30;

    // Only reward win
    const winBonus = gameWin ? 70 : 0;

    const total = revealedScore + timeScore + winBonus;

    // Add final cap so no game exceeds 500
    return Math.round(Math.min(Math.max(total, 0), 500));
  };

  const saveScore = async () => {
    if (!organizationId || !user?.email || !gameWin) return; // prevent saving on loss

    try {
      let userName = "Anonymous";
      if (user.firstName && user.lastName) {
        userName = `${user.firstName} ${user.lastName}`;
      } else if (user.displayName) {
        userName = user.displayName;
      } else if (user.email) {
        userName = user.email.split("@")[0];
      }

      const { score, revealedTiles, totalSafeTiles } =
        calculateMinesweeperScore(board, timeElapsed, difficulty);

      const gameData = {
        gameType: "old-school-minesweeper",
        gameId: "old-school-minesweeper",
        mode: "single",
        userId: user.email,
        userName,
        score,
        timeElapsed,
        revealedTiles,
        totalSafeTiles,
        difficulty,
        timestamp: new Date(),
      };

      const leaderboardDocRef = doc(
        db,
        `organizations/${organizationId}/gameScores`,
        "old-school-minesweeper"
      );
      const userDocRef = doc(
        db,
        `organizations/${organizationId}/users`,
        user.email
      );

      const compositeComparator = (a, b) => {
        if (a.score !== b.score) return b.score - a.score; // higher score wins
        if (a.timeElapsed !== b.timeElapsed)
          return a.timeElapsed - b.timeElapsed;
        return b.revealedTiles - a.revealedTiles;
      };

      await runTransaction(db, async (transaction) => {
        const leaderboardDoc = await transaction.get(leaderboardDocRef);
        let leaderboardData = leaderboardDoc.exists()
          ? leaderboardDoc.data()
          : { leaderboards: {} };

        const diffKey = difficulty.toString();
        let currentLeaderboard = leaderboardData.leaderboards[diffKey] || [];

        if (
          currentLeaderboard.length < 5 ||
          compositeComparator(gameData, currentLeaderboard[4]) < 0
        ) {
          currentLeaderboard.push(gameData);
          currentLeaderboard.sort(compositeComparator);
          currentLeaderboard = currentLeaderboard.slice(0, 5);
          leaderboardData.leaderboards[diffKey] = currentLeaderboard;
          transaction.set(leaderboardDocRef, leaderboardData, { merge: true });
        }

        transaction.update(userDocRef, {
          gameScore: increment(score),
        });
      });

      const gameHistoryRef = doc(
        db,
        `organizations/${organizationId}/users/${
          user.email
        }/gameHistory/${Date.now()}`
      );
      await setDoc(gameHistoryRef, gameData);

      setGameMode("intro");
      setGameStarted(false);
    } catch (err) {
      console.error("Error saving Minesweeper score:", err);
    }
  };

  // ----------------------------------
  //            UTILITIES
  // ----------------------------------
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  const adjacentColors = [
    "",
    "text-blue-400",
    "text-green-400",
    "text-red-500",
    "text-purple-400",
    "text-yellow-300",
    "text-pink-400",
    "text-cyan-300",
    "text-white",
  ];

  // ----------------------------------
  //         SCORING HELPERS
  // ----------------------------------
  const calculateMinesweeperScore = (board, timeElapsed, difficulty) => {
    const totalSafeTiles = rows * cols - getMineCount();
    let revealedSafeTiles = 0;
    for (let row of board) {
      for (let cell of row) {
        if (cell.revealed && !cell.mine) revealedSafeTiles++;
      }
    }

    const baseScore = revealedSafeTiles * 2;
    const timePenalty = timeElapsed * 2;
    const difficultyBonus = difficulty * 5;
    const finalScore = Math.max(0, baseScore + difficultyBonus - timePenalty);
    return { score: finalScore, revealedSafeTiles, totalSafeTiles };
  };

  // ----------------------------------
  //          TUTORIAL LOGIC
  // ----------------------------------
  const [tutorialStep, setTutorialStep] = useState(0);
  const advanceTutorial = () => {
    if (tutorialStep < 2) {
      setTutorialStep((prev) => prev + 1);
    } else {
      setGameMode("play");
      startGame();
    }
  };

  // ----------------------------------
  //              RENDER
  // ----------------------------------
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-950 text-white p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate("/gameroom")}
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back to Gameroom</span>
          </button>
          <div className="flex items-center gap-3">
            <h1 className="text-xl md:text-2xl font-bold text-yellow-300">
              Old School Minesweeper
            </h1>
            <div className="p-2 bg-yellow-500/10 rounded-full">
              <Brain className="text-yellow-400" size={20} />
            </div>
          </div>
          <button
            onClick={() => {}}
            className="p-2 bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors"
          >
            <Info size={20} />
          </button>
        </div>

        {/* Outer Container */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl overflow-hidden">
          {/* Intro Screen */}
          {gameMode === "intro" && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full mx-auto flex items-center justify-center mb-4">
                  <Brain size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Old School Minesweeper
                </h2>
                <p className="text-slate-300 max-w-md mx-auto">
                  Experience the classic Minesweeper puzzle game. Uncover the
                  board, flag mines, and aim for a clear win without triggering
                  any bombs.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-yellow-500/10 rounded-full mb-2">
                    <Brain size={24} className="text-yellow-300" />
                  </div>
                  <h3 className="font-medium text-white mb-1">
                    Classic Puzzle
                  </h3>
                  <p className="text-sm text-slate-300 text-center">
                    Clear the board by revealing safe tiles while avoiding
                    hidden mines.
                  </p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-yellow-500/10 rounded-full mb-2">
                    <Clock size={24} className="text-yellow-300" />
                  </div>
                  <h3 className="font-medium text-white mb-1">
                    Time Challenge
                  </h3>
                  <p className="text-sm text-slate-300 text-center">
                    Race against the clock to clear the board with precision.
                  </p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-yellow-500/10 rounded-full mb-2">
                    <Trophy size={24} className="text-yellow-300" />
                  </div>
                  <h3 className="font-medium text-white mb-1">High Scores</h3>
                  <p className="text-sm text-slate-300 text-center">
                    Compete with others for the fastest completion time.
                  </p>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <div className="flex-1 max-w-xs">
                  <h3 className="font-medium text-white mb-2 text-center md:text-left">
                    Difficulty
                  </h3>
                  <div className="flex gap-2 justify-center md:justify-start">
                    {[1, 2, 3].map((lvl) => (
                      <button
                        key={lvl}
                        onClick={() => setDifficulty(lvl)}
                        className={`flex-1 py-2 px-4 rounded-lg transition-colors ${
                          difficulty === lvl
                            ? "bg-gradient-to-r from-yellow-400 to-orange-500 text-white"
                            : "bg-slate-700 text-slate-300 hover:bg-slate-600"
                        }`}
                      >
                        {lvl === 1 ? "Easy" : lvl === 2 ? "Medium" : "Hard"}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setGameMode("tutorial")}
                    className="flex-1 py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <HelpCircle size={18} />
                    Tutorial
                  </button>

                  <button
                    onClick={() => setGameMode("play")}
                    className="flex-1 py-3 px-6 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    Play Now
                  </button>
                </div>
              </div>

              {highScores.length > 0 && (
                <div className="mt-8">
                  <h3 className="font-medium text-white mb-3 flex items-center gap-2">
                    <Trophy size={18} className="text-yellow-400" />
                    High Scores
                  </h3>
                  <div className="bg-slate-700/30 rounded-lg overflow-x-auto">
                    <table className="w-full text-sm min-w-[500px]">
                      <thead className="bg-slate-700/50">
                        <tr>
                          <th className="py-2 px-4 text-left text-slate-300">
                            Rank
                          </th>
                          <th className="py-2 px-4 text-left text-slate-300">
                            Player
                          </th>
                          <th className="py-2 px-4 text-center text-slate-300">
                            Time
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {highScores.map((s, index) => {
                          let formattedName = s.userName;
                          if (formattedName && formattedName.includes(" ")) {
                            const parts = formattedName.split(" ");
                            const firstName = parts[0];
                            const lastInitial = parts[1]
                              ? parts[1][0] + "."
                              : "";
                            formattedName = `${firstName} ${lastInitial}`;
                          }
                          return (
                            <tr
                              key={`${s.userId}-${index}`}
                              className="border-t border-slate-700/50"
                            >
                              <td className="py-2 px-4 text-slate-300">
                                {index + 1}
                              </td>
                              <td className="py-2 px-4 text-white">
                                {formattedName}
                              </td>
                              <td className="py-2 px-4 text-center text-yellow-300 font-medium">
                                {formatTime(s.timeElapsed)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tutorial Screen */}
          {gameMode === "tutorial" && (
            <div className="p-6 md:p-8">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-white mb-2">
                  Tutorial: Old School Minesweeper
                </h2>
                <div className="bg-slate-700/50 rounded-lg p-4 space-y-4 text-slate-300">
                  {tutorialStep === 0 && (
                    <>
                      <p>
                        Minesweeper is a puzzle game where you reveal cells to
                        find safe spots while avoiding hidden mines.
                      </p>
                      <p>
                        Left-click on a cell to reveal it. If a revealed cell
                        has 0 adjacent mines, neighboring cells are
                        automatically uncovered.
                      </p>
                    </>
                  )}
                  {tutorialStep === 1 && (
                    <>
                      <p>
                        Right-click on a cell to flag it if you suspect there is
                        a mine.
                      </p>
                      <p>
                        The numbers shown indicate the number of mines adjacent
                        to that cell.
                      </p>
                    </>
                  )}
                  {tutorialStep === 2 && (
                    <>
                      <p>
                        Win the game by revealing all cells that do not contain
                        mines.
                      </p>
                      <p>
                        Steer clear of mines—clicking one will immediately end
                        the game.
                      </p>
                    </>
                  )}
                </div>
              </div>
              <div className="flex justify-between">
                {tutorialStep > 0 ? (
                  <button
                    onClick={() => setTutorialStep((prev) => prev - 1)}
                    className="py-2 px-4 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                  >
                    Previous
                  </button>
                ) : (
                  <button
                    onClick={() => setGameMode("intro")}
                    className="py-2 px-4 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                  >
                    Back
                  </button>
                )}

                <button
                  onClick={advanceTutorial}
                  className="py-2 px-4 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white rounded-lg transition-colors"
                >
                  {tutorialStep < 2 ? "Next" : "Start Game"}
                </button>
              </div>
            </div>
          )}

          {/* Play Screen */}
          {gameMode === "play" && (
            <div className="relative p-4 md:p-8">
              {!gameStarted && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/80">
                  <button
                    onClick={() => {
                      setGameStarted(true);
                      startGame();
                    }}
                    className="py-3 px-6 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white rounded-lg"
                  >
                    Start Game
                  </button>
                </div>
              )}

              <AnimatePresence>
                {isPaused && (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-slate-800 px-4 py-2 rounded-lg shadow-lg z-40"
                  >
                    Game Paused
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="flex justify-between items-center mb-4">
                <div className="bg-gradient-to-r from-yellow-400/20 to-orange-500/20 px-3 py-1 rounded-lg">
                  <span className="text-yellow-200 font-medium text-sm md:text-base">
                    Difficulty:{" "}
                    {difficulty === 1
                      ? "Easy"
                      : difficulty === 2
                      ? "Medium"
                      : "Hard"}
                  </span>
                </div>
                <div className="flex items-center">
                  <div className="bg-slate-700/50 px-3 py-1 rounded-lg flex items-center gap-2">
                    <Clock size={16} className="text-slate-400" />
                    <span className="text-white font-mono text-sm">
                      {formatTime(timeElapsed)}
                    </span>
                  </div>
                  <button
                    onClick={() => setIsPaused(!isPaused)}
                    className="ml-2 p-1 md:p-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-300 hover:text-white transition-colors text-sm"
                  >
                    {isPaused ? "Resume" : "Pause"}
                  </button>
                </div>
              </div>

              <div
                className="mx-auto border border-slate-600 rounded-md overflow-hidden shadow-lg"
                style={{ width: "fit-content" }}
              >
                <div
                  className="grid"
                  style={{
                    gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
                    gap: "1px",
                    backgroundColor: "#1e293b",
                  }}
                >
                  {board.flatMap((row, rIdx) =>
                    row.map((cell, cIdx) => {
                      let content = null;
                      let className =
                        "w-8 h-8 sm:w-10 sm:h-10 md:w-11 md:h-11 flex items-center justify-center text-center font-bold text-sm md:text-base border border-slate-600";

                      if (cell.revealed) {
                        if (cell.mine) {
                          className += " bg-red-600 text-white";
                          content = "💣";
                        } else {
                          className += " bg-slate-700";
                          content =
                            cell.adjacent > 0 ? (
                              <span className={adjacentColors[cell.adjacent]}>
                                {cell.adjacent}
                              </span>
                            ) : null;
                        }
                      } else {
                        className +=
                          " bg-slate-900 hover:bg-slate-800 cursor-pointer";
                        content = cell.flagged ? "🚩" : "";
                      }

                      return (
                        <div
                          key={`${rIdx}-${cIdx}`}
                          className={className}
                          onClick={() => handleCellClick(rIdx, cIdx)}
                          onContextMenu={(e) =>
                            handleCellRightClick(e, rIdx, cIdx)
                          }
                        >
                          {content}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              <button
                onClick={endGame}
                className="mt-6 w-full py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg transition-colors text-sm"
              >
                End Game
              </button>
            </div>
          )}

          {/* Results Screen */}
          {gameMode === "results" && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full mx-auto flex items-center justify-center mb-4">
                  <Trophy size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Game Over
                </h2>
                <p className="text-slate-300">
                  {gameOver
                    ? "You hit a mine!"
                    : gameWin
                    ? "Congratulations, you cleared the board!"
                    : "Your Minesweeper session has ended."}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <Trophy size={20} className="text-yellow-300" />
                    Your Results
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Score</span>
                      <span className="text-yellow-300 font-bold text-xl">
                        {score}
                      </span>
                    </div>

                    <div className="flex justify-between items-center pb-2 border-b border-slate-700">
                      <span className="text-slate-300">Time</span>
                      <span className="text-2xl font-bold text-yellow-300">
                        {formatTime(timeElapsed)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Difficulty</span>
                      <span className="text-white font-medium">
                        {difficulty === 1
                          ? "Easy"
                          : difficulty === 2
                          ? "Medium"
                          : "Hard"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <Brain size={20} className="text-yellow-300" />
                    Cognitive Benefits
                  </h3>
                  <div className="space-y-3 text-slate-300">
                    <p>
                      <span className="text-yellow-300 font-medium">
                        Logical Reasoning:
                      </span>{" "}
                      Deduce which cells are safe based on the numbers provided.
                    </p>
                    <p>
                      <span className="text-yellow-300 font-medium">
                        Planning & Strategy:
                      </span>{" "}
                      Flag potential mines and carefully decide your next move.
                    </p>
                    <p>
                      <span className="text-yellow-300 font-medium">
                        Pattern Recognition:
                      </span>{" "}
                      Recognize common mine patterns to make informed decisions.
                    </p>
                    <p className="mt-4 text-sm">
                      A steady mind and careful planning are the keys to
                      success.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mb-6 bg-slate-800/50 rounded-lg p-4 text-sm text-slate-300">
                <h3 className="text-white font-medium mb-2">
                  How Scoring Works
                </h3>
                <p>
                  Scoring combines how many safe tiles you reveal and how
                  quickly you finish.
                  <br />
                  Faster clears with more reveals = higher score. Max: ~500+
                </p>
              </div>

              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <button
                  onClick={() => setGameMode("intro")}
                  className="py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                >
                  Back to Menu
                </button>

                <button
                  onClick={saveScore}
                  className="py-3 px-6 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  <Trophy size={18} />
                  Save Score
                </button>

                <button
                  onClick={() => {
                    setGameMode("play");
                    startGame();
                  }}
                  className="py-3 px-6 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  Play Again
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Minesweeper;
